import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Snackbar,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { Timestamp } from "firebase/firestore";
import useFirestore from "../../../../config/fireBaseHook";
import { TpCreditTypeDropdown } from "../../CourseCatalog/DropDowns";
import {
  getCurrentSchoolYear,
  isValidSchoolYear,
} from "../../../../utils/schoolYearUtils";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const Add = ({ onRecordAdded }) => {
  const { addData } = useFirestore("CreditCapture");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [formData, setFormData] = useState({
    CTAN: "",
    Tartan_ID: "",
    Course: "",
    CourseAndSubject: "",
    Credits: "",
    Grade: "",
    Course_School_Year: getCurrentSchoolYear(),
    CreditType: "",
    Subject: "",
    TP_Section: "",
    CTAN_Earned_Date: Timestamp.now(),
    CreditCaptureKey: "",
    InCountyCostSavings: "",
    Note: "",
    OtherCountyCostSavings: "",
    PLTW_credit: 0,
    PermissionToPostCredit: 0,
    Posted: 0,
    School_Year_Posted: "",
    course_inst_id: "",
    odhe_ctag_ptp: "",
    permission_response: "No Response",
    sent_to_rsr: "",
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.CTAN.trim()) {
      newErrors.CTAN = "CTAN is required";
    }

    if (!formData.Tartan_ID) {
      newErrors.Tartan_ID = "Tartan ID is required";
    }

    if (!formData.Course.trim()) {
      newErrors.Course = "Course is required";
    }

    if (!formData.Credits || formData.Credits <= 0) {
      newErrors.Credits = "Credits must be a positive number";
    }

    if (!formData.Grade.trim()) {
      newErrors.Grade = "Grade is required";
    }

    if (!isValidSchoolYear(formData.Course_School_Year)) {
      newErrors.Course_School_Year =
        "Please enter a valid school year (format: YYYY-YYYY)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]:
        name === "Credits" ||
        name === "Tartan_ID" ||
        name === "InCountyCostSavings" ||
        name === "OtherCountyCostSavings" ||
        name === "PLTW_credit" ||
        name === "PermissionToPostCredit" ||
        name === "Posted" ||
        name === "course_inst_id"
          ? Number(value) || 0
          : value,
    };

    // Auto-combine Course and Subject when either changes
    if (name === "Course" || name === "Subject") {
      const courseValue = name === "Course" ? value : formData.Course;
      const subjectValue = name === "Subject" ? value : formData.Subject;
      if (courseValue && subjectValue) {
        newFormData.CourseAndSubject = `${courseValue}: ${subjectValue}`;
      }
    }

    setFormData(newFormData);

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const dataToSubmit = {
        ...formData,
        CTAN_Earned_Date: Timestamp.now(),
        Time_Updated_Stamp: Timestamp.now(),
      };

      await addData(dataToSubmit);
      setOpen(false);
      setFormData({
        CTAN: "",
        Tartan_ID: "",
        Course: "",
        CourseAndSubject: "",
        Credits: "",
        Grade: "",
        Course_School_Year: getCurrentSchoolYear(),
        CreditType: "",
        Subject: "",
        TP_Section: "",
        CTAN_Earned_Date: Timestamp.now(),
        CreditCaptureKey: "",
        InCountyCostSavings: "",
        Note: "",
        OtherCountyCostSavings: "",
        PLTW_credit: 0,
        PermissionToPostCredit: 0,
        Posted: 0,
        School_Year_Posted: "",
        course_inst_id: "",
        odhe_ctag_ptp: "",
        permission_response: "No Response",
        sent_to_rsr: "",
      });
      setErrors({});
      if (onRecordAdded) onRecordAdded();
    } catch (error) {
      console.error("Error adding credit capture:", error);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      CTAN: "",
      Tartan_ID: "",
      Course: "",
      CourseAndSubject: "",
      Credits: "",
      Grade: "",
      Course_School_Year: getCurrentSchoolYear(),
      CreditType: "",
      Subject: "",
      TP_Section: "",
      CTAN_Earned_Date: Timestamp.now(),
      CreditCaptureKey: "",
      InCountyCostSavings: "",
      Note: "",
      OtherCountyCostSavings: "",
      PLTW_credit: 0,
      PermissionToPostCredit: 0,
      Posted: 0,
      School_Year_Posted: "",
      course_inst_id: "",
      odhe_ctag_ptp: "",
      permission_response: "No Response",
      sent_to_rsr: "",
    });
    setErrors({});
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleClickOpen}>
        Add Record
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleClose}>
          Add New Credit Capture
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoFocus
                margin="dense"
                name="CTAN"
                label="CTAN"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.CTAN}
                onChange={handleChange}
                error={!!errors.CTAN}
                helperText={errors.CTAN}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="Tartan_ID"
                label="Tartan ID"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.Tartan_ID}
                onChange={handleChange}
                error={!!errors.Tartan_ID}
                helperText={errors.Tartan_ID}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="Course"
                label="Course"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.Course}
                onChange={handleChange}
                error={!!errors.Course}
                helperText={errors.Course}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="Credits"
                label="Credits"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.Credits}
                onChange={handleChange}
                error={!!errors.Credits}
                helperText={errors.Credits}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="Grade"
                label="Grade"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.Grade}
                onChange={handleChange}
                error={!!errors.Grade}
                helperText={errors.Grade}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="Course_School_Year"
                label="School Year (YYYY-YYYY)"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.Course_School_Year}
                onChange={handleChange}
                error={!!errors.Course_School_Year}
                helperText={errors.Course_School_Year || "Format: 2024-2025"}
                placeholder="2024-2025"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TpCreditTypeDropdown
                name="CreditType"
                value={formData.CreditType}
                onChange={handleChange}
                label="Credit Type"
                margin="dense"
                required={false}
              />
              {errors.CreditType && (
                <div
                  style={{
                    color: "#d32f2f",
                    fontSize: "0.75rem",
                    marginLeft: "14px",
                    marginTop: "3px",
                  }}
                >
                  {errors.CreditType}
                </div>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="Subject"
                label="Subject"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.Subject}
                onChange={handleChange}
                error={!!errors.Subject}
                helperText={errors.Subject}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="CourseAndSubject"
                label="Course & Subject (Auto-Generated)"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.CourseAndSubject}
                onChange={handleChange}
                disabled
                error={!!errors.CourseAndSubject}
                helperText={
                  errors.CourseAndSubject ||
                  "This field is auto-generated from Course and Subject"
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="TP_Section"
                label="TP Section"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.TP_Section}
                onChange={handleChange}
                error={!!errors.TP_Section}
                helperText={errors.TP_Section}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="CreditCaptureKey"
                label="Credit Capture Key"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.CreditCaptureKey}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="InCountyCostSavings"
                label="In County Cost Savings"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.InCountyCostSavings}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="Note"
                label="Note"
                type="text"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={formData.Note}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="OtherCountyCostSavings"
                label="Other County Cost Savings"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.OtherCountyCostSavings}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="PLTW_credit"
                label="PLTW Credit"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.PLTW_credit}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="PermissionToPostCredit"
                label="Permission To Post Credit"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.PermissionToPostCredit}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="Posted"
                label="Posted"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.Posted}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="School_Year_Posted"
                label="School Year Posted"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.School_Year_Posted}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="course_inst_id"
                label="Course Inst ID"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.course_inst_id}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="odhe_ctag_ptp"
                label="ODHE CTAG PTP"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.odhe_ctag_ptp}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Permission Response</InputLabel>
                <Select
                  name="permission_response"
                  value={formData.permission_response}
                  onChange={handleChange}
                  label="Permission Response"
                >
                  <MenuItem value="No Response">No Response</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="sent_to_rsr"
                label="Sent to RSR"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.sent_to_rsr}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions onSave={handleSubmit} saving={loading} saveLabel="Add" />
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message="Error adding credit capture"
      />
    </>
  );
};

export default Add;
